import * as React from 'react';
import l10n from '../../util/l10n';
import feature = require('../../util/features');

import {AviaryEditorSaveData} from '../../aviary/AviaryEditorDialog';
import DiyAviaryEditorDialog from '../../aviary/DiyAviaryEditorDialog';
import ModulImageSubtitle from '../ModulImageSubtitle';
import ModulTextWithImage from '../ModulTextWithImage';
import {BaseViewProps, ImageViewProps, ImageWithTextViewProps} from './interfaces';
import {ImageModuleBaseData, ImageModuleData} from "./interfaces";
import {getFitPayload} from '../../image-inline-editor/Utils';
import Warning from '../../components/Warning';
import {getCachedImage, clearCachedImage} from '../../util/image/cache';
import {getIieSourceImageUrl, getIieSourceImageWidth, getIieSourceImageHeight} from './util';

import * as Groups from './Groups';
import ResponsiveBar from '../../components/ResponsiveBar';

import * as common from './common';

interface ImageEditViewState {
    aviaryEditing: boolean;
}

export default class ImageEditView extends React.Component<BaseViewProps, ImageEditViewState> {
    static MIN_IMAGE_WIDTH = 10;
    static MIN_IMAGE_HEIGHT = 10;
    static IMAGE_RESIZE_STEP = 5;

    constructor(props: BaseViewProps) {
        super(props);
        this.state = {
            aviaryEditing: false
        };
    }

    componentDidMount() {
        const modul = this.props.module;

        if (modul.getIsNew() && modul.autoOpenImageSelector) {
            if (modul instanceof ModulTextWithImage) {
                modul.getCKEditorPromise().then(this.handleSelectImage);
            } else {
                this.handleSelectImage();
            }
        }
        modul.autoOpenImageSelector = false;
    }

    render() {
        const props = this.props as BaseViewProps;
        const data = this.props.payload;
        const hasAlign = (this.hasImageProps(props) && props.payload.supports.imageAlign)
            || this.hasTextProps(props);
        const hasResize = (this.hasImageProps(props) && props.payload.supports.imageResize)
            || this.hasTextProps(props);
        const hasFullwidth = this.isFullwidthAvailable();
        const hasAlignCenter = !this.hasTextProps(props);
        const imageUpscaled = common.getIieModel(data, this.props.module).isUpscaled();
        return (
            <div>
                <div>
                    <ResponsiveBar>
                        {Groups.getBaseButtons({
                            handleSelect: this.handleSelectImage,
                            handleEdit: this.handleEditImage,
                            editEnabled: !this.props.payload.imageAnimated
                        })
                        }
                        {
                            hasAlign ?
                            Groups.getAlignButtons({
                                hasAlignCenter: hasAlignCenter,
                                position: data.imagePosition,
                                disabled: this.isFullwidth() || data.imageFit,
                                handler: this.handleAlign
                            })
                            : null
                        }
                        {
                            hasResize ?
                            Groups.getResizeButtons({
                                hasFullwidth: hasFullwidth,
                                minusEnabled: this.sizeMinusEnabled(),
                                plusEnabled: this.sizePlusEnabled(),
                                fitEnabled: this.isFitEnabled(),
                                fullwidthEnabled: this.isFullwidthEnabled(),
                                fullwidthPressed: !!this.isFullwidth(),
                                handleMinus: this.handleSizeMinus,
                                handlePlus: this.handleSizePlus,
                                handleFit: this.handleFit,
                                handleFullwidth: this.handleFullwidth,
                                handleResetSize: this.handleResetSize
                            })
                            : null
                        }
                        {
                        Groups.getLinkButton({
                            hasLink: !!data.link,
                            handler: this.handleLinkClick
                        })
                        }
                    </ResponsiveBar>
                    {this.state.aviaryEditing ? this.renderAviary() : null}
                </div>
                {imageUpscaled ? <Warning text={l10n("module.image.messages.upscale")} /> : null}
            </div>
        );
    }

    private renderAviary() {
        /* @todo: here we should branch to different editors, to remove aviary dependency */
        return (
            <DiyAviaryEditorDialog
                imageUrl={getIieSourceImageUrl(this.props.payload)}
                resultFileFormat={
                    (this.props.payload.imageMediaType === 'jpg' || this.props.payload.imageMediaType === 'jpeg')
                    ? 'jpg' : 'png'
                }
                originalImageUrl={this.props.payload.origImageUrl}
                onClose={this.onAviaryClose}
                onSave={this.onAviarySave}
            />
        );
    }

    private hasImageProps(props: BaseViewProps): props is ImageViewProps {
        return !this.props.module.hasText;
    }

    private hasTextProps(props: BaseViewProps): props is ImageWithTextViewProps {
        return this.props.module.hasText;
    }

    private isFullwidth(props: BaseViewProps = null) {
        const p = props || (this.props as BaseViewProps);
        return this.hasImageProps(p) && p.payload.isFullwidth;
    }

    private isFullwidthEnabled() {
        return true;
    }

    private isFullwidthAvailable() {
        const props = this.props as BaseViewProps;
        return feature("fullwidth")
            && !props.module.inSidebar()
            && this.hasImageProps(props)
            && props.payload.supports.imageFullWidth;
    }

    private isFitEnabled() {
        return !this.isFullwidth() && !this.props.payload.imageFit;
    }

    private handleSelectImage = () => {
        common.handleSelectImage(
            this.props.moduleId,
            this.props.module,
            this.props.payload,
            (newData: ImageModuleBaseData) => {
                this.props.onDataChange(newData);
            }
        );
    };

    private handleEditImage = () => {
        const newState = {} as ImageEditViewState;
        newState.aviaryEditing = true;
        this.setState(newState);
    };

    private handleAlign = (e: React.SyntheticEvent<{}>, index: number) => {
        const props = this.props as BaseViewProps;
        const hasAlignCenter = !this.hasTextProps(props);
        let position: number;
        if (hasAlignCenter) {
            switch (index) {
                case 1: position = 3; break;
                case 2: position = 2; break;
                default: position = 1; break;
            }
        } else {
            switch (index) {
                case 1: position = 2; break;
                default: position = 1; break;
            }
        }

        const newData = {} as ImageModuleBaseData;
        newData.imagePosition = position;
        this.props.onDataChange(newData);
    };

    private handleSizeMinus = () => {
        if (this.sizeMinusEnabled()) {
            this.changeSizeWith(- ImageEditView.IMAGE_RESIZE_STEP);
        }
    };

    private handleSizePlus = () => {
        if (this.sizePlusEnabled()) {
            this.changeSizeWith(ImageEditView.IMAGE_RESIZE_STEP);
        }
    };

    private handleResetSize = () => {
        common.handleResetSize(this.props.payload, this.props.module, (newData: ImageModuleBaseData) => {
            this.props.onDataChange(newData);
        });
    };

    private changeSizeWith(amount: number) {
        let newWidth: number;
        let newHeight: number;
        const payload = this.props.payload;
        if (this.isFullwidth()) {
            newWidth = payload.visualImageWidth;
            newHeight = payload.visualImageHeight + amount;
        } else {
            newWidth = Math.min(this.props.module.getMaxWidth(), payload.visualImageWidth + amount);
            newHeight = newWidth * payload.visualImageHeight / payload.visualImageWidth;
        }
        this.resize(newWidth, newHeight, false);
    }

    private handleFit = () => {
        const newWidth = this.props.module.getMaxWidth();
        const aspectRatio = this.props.payload.visualImageWidth / this.props.payload.visualImageHeight;
        const newHeight = newWidth / aspectRatio;
        this.resize(newWidth, newHeight, true);
    };

    private handleFullwidth = (e: React.SyntheticEvent<{}>, isPressed: boolean) => {
        const fullwidth = isPressed;
        const data = this.props.payload;

        (this.props.module as ModulImageSubtitle).setFullwidth(fullwidth);

        const newData = Object.assign({}, this.props.payload) as ImageModuleData;
        newData.isFullwidth = fullwidth;

        newData.imageWidth = this.props.module.getMaxWidth();
        newData.visualImageWidth = this.props.module.getMaxWidth();
        if (data.imageAnimated) {
            const aspect = data.origImageHeight / data.origImageWidth;
            newData.visualImageHeight = aspect * newData.visualImageWidth;
        }

        newData.iiePayload = getFitPayload(
            getIieSourceImageWidth(this.props.payload),
            getIieSourceImageHeight(this.props.payload),
            newData.visualImageWidth,
            newData.visualImageHeight
        );
        this.props.onDataChange(newData);
    };

    private handleLinkClick = () => {
        common.handleAddLink(this.props.payload, (newData: ImageModuleBaseData) => {
            this.props.onDataChange(newData);
        });
    };

    private onAviaryClose = () => {
        const newState = {} as ImageEditViewState;
        newState.aviaryEditing = false;
        this.setState(newState);
    };

    private onAviarySave = (aviaryData: AviaryEditorSaveData) => {
        const newState = {} as ImageEditViewState;
        newState.aviaryEditing = false;
        this.setState(newState);

        const imageKey = 'transformedImageUrl' + this.props.moduleId;
        // clear first in case there are previous versions
        clearCachedImage(imageKey);
        getCachedImage(
            aviaryData.imageDataUrl, imageKey
        ).then((image: HTMLImageElement) => {
            const data = this.props.payload;
            const newData = {} as ImageModuleBaseData;
            newData.transformedImageUrl = aviaryData.imageDataUrl;
            newData.aviaryActionList = aviaryData.actionList;

            // fixing the canvas size
            if (
                image.height !== newData.transformedImageHeight
                || image.width !== newData.transformedImageWidth
            ) {
                // calculating the image height using cross product
                newData.visualImageWidth = data.imageWidth;
                newData.visualImageHeight = data.imageWidth * (image.height / image.width);
            }

            newData.transformedImageWidth = image.width;
            newData.transformedImageHeight = image.height;

            if (
                newData.transformedImageHeight !== getIieSourceImageHeight(this.props.payload)
                || newData.transformedImageWidth !== getIieSourceImageWidth(this.props.payload)
            ) {
                // so there was not only adding some effects, but dimensions changed
                // in that case we need to reset iiePayload to have a best fit
                // but keeping visual size of the image

                newData.iiePayload = getFitPayload(
                    newData.transformedImageWidth,
                    newData.transformedImageHeight,
                    data.visualImageWidth,
                    data.visualImageHeight
                );
            }

            this.props.onDataChange(newData);
        });
    };

    private sizeMinusEnabled(): boolean {
        const data = this.props.payload;
        const step = ImageEditView.IMAGE_RESIZE_STEP;
        const min = ImageEditView.MIN_IMAGE_HEIGHT;
        if (this.isFullwidth()) {
            return !data.imageAnimated && data.visualImageHeight - step >= min;
        } else {
            return data.visualImageHeight - step >= min && data.visualImageWidth - step >= min;
        }
    }

    private sizePlusEnabled(): boolean {
        const data = this.props.payload;
        if (this.isFullwidth()) {
            return true;
        } else {
            return data.visualImageWidth < this.props.module.getMaxWidth();
        }
    }

    private resize(width: number, height: number, keepZoom?: boolean, additionalData?: ImageModuleBaseData) {
        const model = common.getIieModel(this.props.payload, this.props.module);
        model.resize(width, height, keepZoom);

        this.props.onDataChange(Object.assign({}, {
            imageWidth: width,
            visualImageWidth: width,
            imageHeight: height,
            visualImageHeight: height,
            imageFit: width >= this.props.module.getMaxWidth(),
            iiePayload: model.getPayload()
        }, additionalData));
    }
}



/** WEBPACK FOOTER **
 ** ./build/js/~/tslint-loader!./source/cdn/document_root/s/diy/module/image/EditView.tsx
 **/